import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaHeart,
  FaRegHeart,
  FaTimes,
  FaArrowLeft,
  FaShieldAlt,
  FaRocket,
  FaBook,
  FaGraduationCap,
} from "react-icons/fa";
import { MdSort } from "react-icons/md";
import { HiOutlineSparkles } from "react-icons/hi";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import NoteCard from "../components/notes/NoteCard";
import Loader from "../components/common/Loader";
import { useFooter } from "../context/FooterContext";

const Notes = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { setShowFooter } = useFooter();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    branch: searchParams.get("branch") || "",
    semester: searchParams.get("semester") || "",
    subject: searchParams.get("subject") || "",
    module: searchParams.get("module") || "",
    search: searchParams.get("search") || "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("latest");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const branches = ["CSE", "ISE", "ECE", "EEE", "MECH", "CIVIL", "AIML", "DS"];
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];
  const modules = [1, 2, 3, 4, 5];

  // Hide footer when component mounts
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error("Please login to access study materials");
      navigate("/login");
      return;
    }
    fetchNotes();
  }, [filters, sortBy, pagination.currentPage, isAuthenticated, navigate]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        ...filters,
        sort: sortBy,
        page: pagination.currentPage,
        limit: 12,
      });

      const response = await axios.get(`/api/notes?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setNotes(response.data.notes);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        total: response.data.total,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Please login to view notes");
        navigate("/login");
      } else {
        toast.error("Failed to fetch notes");
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setSearchParams(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilterChange("search", e.target.search.value);
  };

  const handleLike = async (noteId) => {
    if (!user) {
      toast.error("Please login to like notes");
      return;
    }
    try {
      await axios.post(`/api/notes/${noteId}/like`);
      fetchNotes();
    } catch (error) {
      toast.error("Failed to like note");
    }
  };

  const clearAllFilters = () => {
    setFilters({
      branch: "",
      semester: "",
      subject: "",
      module: "",
      search: "",
    });
    setSearchParams({});
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "calc(100vh - 200px)",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#8b5cf6",
            background: "none",
            border: "none",
            cursor: "pointer",
            marginBottom: "1.5rem",
            fontSize: "0.9rem",
            fontWeight: 500,
            padding: "0.5rem 0",
            transition: "all 0.3s",
          }}
          onMouseEnter={(e) => (e.target.style.transform = "translateX(-5px)")}
          onMouseLeave={(e) => (e.target.style.transform = "translateX(0)")}
        >
          <FaArrowLeft /> Back to Dashboard
        </button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            textAlign: "center",
            marginBottom: "2rem",
          }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            style={{ display: "inline-block", marginBottom: "1rem" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                width: "60px",
                height: "60px",
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
              }}
            >
              <FaBook size={28} color="white" />
            </div>
          </motion.div>
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "2.5rem",
              fontWeight: "bold",
              marginBottom: "0.5rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Study <span style={{ color: "#8b5cf6" }}>Notes</span>
          </h1>
          <p style={{ color: getTextSecondary() }}>
            Browse through thousands of notes shared by VTU students
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSearch}
          style={{ marginBottom: "2rem" }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              padding: "0.5rem",
              borderRadius: "60px",
              boxShadow: isDarkMode
                ? "0 25px 50px -12px rgba(0,0,0,0.5)"
                : "0 25px 50px -12px rgba(0,0,0,0.25)",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <input
              type="text"
              name="search"
              placeholder="Search notes by title, subject, or tags..."
              defaultValue={filters.search}
              style={{
                flex: 1,
                padding: "1rem 1.5rem",
                border: "none",
                outline: "none",
                fontSize: "0.95rem",
                background: "transparent",
                color: getTextColor(),
              }}
              onFocus={(e) => (e.target.placeholder = "")}
              onBlur={(e) =>
                (e.target.placeholder =
                  "Search notes by title, subject, or tags...")
              }
            />
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              style={{
                padding: "1rem 2rem",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "50px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.9rem",
                fontWeight: "600",
                boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
              }}
            >
              <FaSearch /> Search
            </motion.button>
          </div>
        </motion.form>

        {/* Filters Toggle */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          onClick={() => setShowFilters(!showFilters)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.7rem 1.5rem",
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            border: `1px solid ${getBorderColor()}`,
            borderRadius: "40px",
            marginBottom: "1rem",
            cursor: "pointer",
            color: showFilters ? "#8b5cf6" : getTextColor(),
            fontWeight: "500",
            transition: "all 0.3s",
          }}
        >
          <FaFilter /> {showFilters ? "Hide Filters" : "Show Filters"}
        </motion.button>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                padding: "2rem",
                borderRadius: "24px",
                marginBottom: "2rem",
                border: `1px solid ${getBorderColor()}`,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1.5rem",
                }}
              >
                {/* Branch Filter */}
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      fontSize: "0.85rem",
                    }}
                  >
                    <FaGraduationCap
                      style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                    />
                    Branch
                  </label>
                  <select
                    value={filters.branch}
                    onChange={(e) =>
                      handleFilterChange("branch", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "0.8rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "12px",
                      outline: "none",
                      fontSize: "0.9rem",
                      background: getInputBackground(),
                      color: getTextColor(),
                      cursor: "pointer",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  >
                    <option value="">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch} value={branch}>
                        {branch}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Semester Filter */}
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      fontSize: "0.85rem",
                    }}
                  >
                    <FaGraduationCap
                      style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                    />
                    Semester
                  </label>
                  <select
                    value={filters.semester}
                    onChange={(e) =>
                      handleFilterChange("semester", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "0.8rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "12px",
                      outline: "none",
                      fontSize: "0.9rem",
                      background: getInputBackground(),
                      color: getTextColor(),
                      cursor: "pointer",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  >
                    <option value="">All Semesters</option>
                    {semesters.map((sem) => (
                      <option key={sem} value={sem}>
                        Semester {sem}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Module Filter */}
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      fontSize: "0.85rem",
                    }}
                  >
                    <FaBook
                      style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                    />
                    Module
                  </label>
                  <select
                    value={filters.module}
                    onChange={(e) =>
                      handleFilterChange("module", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "0.8rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "12px",
                      outline: "none",
                      fontSize: "0.9rem",
                      background: getInputBackground(),
                      color: getTextColor(),
                      cursor: "pointer",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  >
                    <option value="">All Modules</option>
                    {modules.map((module) => (
                      <option key={module} value={module}>
                        Module {module}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sort By */}
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      fontSize: "0.85rem",
                    }}
                  >
                    <MdSort
                      style={{ marginRight: "0.3rem", color: "#8b5cf6" }}
                    />
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "0.8rem",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "12px",
                      outline: "none",
                      fontSize: "0.9rem",
                      background: getInputBackground(),
                      color: getTextColor(),
                      cursor: "pointer",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = getBorderColor())
                    }
                  >
                    <option value="latest">Latest</option>
                    <option value="popular">Most Popular</option>
                    <option value="downloaded">Most Downloaded</option>
                    <option value="rated">Highest Rated</option>
                  </select>
                </div>
              </div>

              {/* Clear Filters */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={clearAllFilters}
                style={{
                  marginTop: "1.5rem",
                  padding: "0.6rem 1.2rem",
                  background: "none",
                  border: `2px solid #8b5cf6`,
                  color: "#8b5cf6",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "0.85rem",
                  transition: "all 0.3s",
                }}
              >
                Clear Filters
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Notes Grid */}
        {loading ? (
          <Loader />
        ) : notes.length > 0 ? (
          <>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "1.5rem",
                marginBottom: "2rem",
              }}
            >
              {notes.map((note, idx) => (
                <motion.div
                  key={note._id}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                >
                  <NoteCard
                    note={note}
                    onLike={handleLike}
                    isDarkMode={isDarkMode}
                  />
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginTop: "2rem",
                  flexWrap: "wrap",
                }}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage - 1,
                    }))
                  }
                  disabled={pagination.currentPage === 1}
                  style={{
                    padding: "0.5rem 1rem",
                    background:
                      pagination.currentPage === 1
                        ? isDarkMode
                          ? "rgba(139,92,246,0.2)"
                          : "#e2e8f0"
                        : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color:
                      pagination.currentPage === 1
                        ? getTextSecondary()
                        : "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor:
                      pagination.currentPage === 1 ? "not-allowed" : "pointer",
                    fontWeight: "500",
                  }}
                >
                  Previous
                </motion.button>
                <span
                  style={{
                    padding: "0.5rem 1rem",
                    color: getTextColor(),
                    fontWeight: "500",
                  }}
                >
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: prev.currentPage + 1,
                    }))
                  }
                  disabled={pagination.currentPage === pagination.totalPages}
                  style={{
                    padding: "0.5rem 1rem",
                    background:
                      pagination.currentPage === pagination.totalPages
                        ? isDarkMode
                          ? "rgba(139,92,246,0.2)"
                          : "#e2e8f0"
                        : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color:
                      pagination.currentPage === pagination.totalPages
                        ? getTextSecondary()
                        : "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor:
                      pagination.currentPage === pagination.totalPages
                        ? "not-allowed"
                        : "pointer",
                    fontWeight: "500",
                  }}
                >
                  Next
                </motion.button>
              </motion.div>
            )}
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              textAlign: "center",
              padding: "4rem",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                background: isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)",
                width: "80px",
                height: "80px",
                borderRadius: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem auto",
              }}
            >
              <FaBook size={40} color="#8b5cf6" />
            </div>
            <h3 style={{ color: getTextColor(), marginBottom: "0.5rem" }}>
              No notes found
            </h3>
            <p style={{ color: getTextSecondary() }}>
              Try adjusting your filters or search query
            </p>
          </motion.div>
        )}

        {/* Security Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: "2rem",
            padding: "1rem",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
            All notes are verified for quality and relevance
          </span>
        </motion.div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Notes;
